<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue';
import { VIcon } from 'vuetify/components/VIcon';
import { VDataTableVirtual } from 'vuetify/components/VDataTable';
import { useLocale } from '../../composables/useLocale';
import { UserToCreate } from './useCreateMultipleUsers';
import EditableText from './EditableText.vue';
import SelectIcon from './SelectIcon.vue';
import GroupAssignment from './GroupAssignment.vue';
import { isGroupLevelRestriction } from '@ilteducation/customer-mgmt-permissions';
import { generateQrToken, generateRandomPassword, isEmployeeOrAdmin } from '../../utils';
import { emailRule, fullnameRules, groupRule } from '../Conflict/useConflictManager';
import { Conflict } from '../Conflict/types';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { RoleOption, useUserRoles } from '../../composables/useUserRoles';
import RoleSelect from '../RoleSelect.vue';

const { t } = useLocale();

type Props = {
  items: UserToCreate[];
  loading?: boolean;
  readonly?: boolean;
  conflicts?: Conflict[];
  canRetryCreate?: boolean;
  organization: SlimOrganization;
};

const { getRoleOptionsToCreateWith } = useUserRoles();

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  readonly: false,
  canRetryCreate: false,
});

const roleOptions = ref<RoleOption[]>([]);
watch(
  props.organization,
  async () => {
    return getRoleOptionsToCreateWith(props.organization).then((options) => {
      roleOptions.value = options;
    });
  },
  { immediate: true },
);
const data = toRef(props, 'items');
const isGroupLevel = computed(() => isGroupLevelRestriction(props.organization));

const emits = defineEmits<{
  (event: 'retryCreateSingleUser', value: UserToCreate): void;
  (event: 'uploadUsers'): void;
  (event: 'removeUser', key: string): void;
  (event: 'userUpdated', value: UserToCreate): void;
  (event: 'rowUpdated', index: number): void;
  (event: 'openResolveConfilctModal', value: Conflict): void;
}>();

const updateName = (item: UserToCreate) => {
  emits('userUpdated', item);
};
const updateEmail = (item: UserToCreate) => {
  item.email = (item?.email || '').trim();
  emits('userUpdated', item);
};

const addNewUser = () => {
  const newUser: UserToCreate = {
    key: generateRandomPassword(),
    name: '',
    role: 'student',
    email: '',
    status: 'pending' as UserToCreate['status'],
    password: generateRandomPassword(),
    qrToken: generateQrToken(),
    errorMessages: '',
    validations: {},
    groupUid: undefined,
  };
  data.value.push(newUser);
};

const onBulkGroupAssign = (uid: string | undefined, name: string | undefined) => {
  data.value.forEach((user) => {
    user.groupUid = uid;
    user.groupName = name;
  });
};

const searchString = ref('');
const setSearchString = (value: string) => {
  searchString.value = value;
};

const onModelValueUpdated = (val: string | undefined | null, index: number) => {
  emits('rowUpdated', index);
};

const isReadonly = (item: UserToCreate) => {
  return item.status !== 'error' && (!!item.uid || props.readonly || item.status === 'success');
};

const openResolveCurrentConfilctModal = (conflict: Conflict) => {
  emits('openResolveConfilctModal', conflict);
};

const existingUserTooltipMessage = (user: UserToCreate) => {
  if (user.uid) {
    if (isEmployeeOrAdmin(user.role)) {
      return t('lcp.user_will_not_change_but_activated', { name: user.name, role: t(`lcp.role_${user.role}`) });
    }
    return t('lcp.user_will_be_updated', { name: user.name });
  }

  return '';
};
</script>
<template>
  <v-row class="ga-6" no-gutters>
    <v-col cols="12">
      <v-row>
        <v-col cols="auto" class="d-flex flex-1-0">
          <v-text-field
            v-model="searchString"
            :label="t('lcp.search_for_user')"
            append-inner-icon="mdi-magnify"
            full-width
            single-line
            hide-details
            @click:append-inner="setSearchString(searchString)"
          ></v-text-field>
        </v-col>
        <v-col cols="auto">
          <v-btn :disabled="readonly" @click="emits('uploadUsers')">{{ t('lcp.upload_users') }}</v-btn>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12">
      <v-data-table-virtual
        :headers="[
          { title: t('lcp.name'), key: 'name', align: 'start', sortable: true },
          { title: t('lcp.role'), key: 'role', align: 'start', sortable: true },
          { title: t('lcp.email'), key: 'email', align: 'start', sortable: true },
          { title: t('lcp.group'), key: 'group', align: 'start', sortable: false },
          { title: '', key: 'actions', align: 'end', sortable: false },
        ]"
        :loading="loading"
        :header-props="{ style: `border-bottom: 1px solid rgb(var(--v-theme-outline)) !important;` }"
        hide-no-data
        fixed-header
        :items="data"
        item-value="key"
        :search="searchString"
        show-expands
        hide-default-footer
        height="400px"
      >
        <template #header.group>
          <GroupAssignment
            :organization-uid="organization.uid"
            :label="t('lcp.group')"
            :disabled="readonly"
            :is-header="true"
            :header-tooltip="t('lcp.assign_group_for_all_users')"
            @group-selected="({ uid, name }) => onBulkGroupAssign(uid, name)"
          />
        </template>
        <template #item="{ item, index, toggleExpand, internalItem }">
          <tr
            class="border-1"
            :class="{
              'opacity-60': item.status === 'checkingExistingUsers',
            }"
            :style="{
              backgroundColor: item.status === 'error' ? 'rgba(var(--v-theme-error), 0.1)' : '',
              borderBottom: '1px solid rgba(var(--v-theme-outline), 0.12)',
            }"
          >
            <td>
              <div class="d-flex align-center">
                <EditableText
                  v-model="item.name"
                  :key="item.key"
                  :readonly="isReadonly(item)"
                  @blur="updateName(item)"
                  :rules="fullnameRules(item, t)"
                  @editing="(editing) => (item.editing = editing)"
                  @updated="($event) => onModelValueUpdated($event, index)"
                  :errorMessages="item.validations?.['invalid-name']?.message"
                />

                <v-tooltip location="top" :text="existingUserTooltipMessage(item)">
                  <template v-slot:activator="{ props }">
                    <v-icon v-if="isReadonly(item) && item.uid" v-bind="props" color="primary">mdi-lock</v-icon>
                  </template>
                </v-tooltip>

                <template v-if="!item.editing">
                  <div class="d-flex ga-2">
                    <v-icon v-if="item.status === 'checkingExistingUsers'" color="primary">mdi-loading mdi-spin</v-icon>
                    <v-chip
                      v-if="item.validations?.['duplicate']"
                      prepend-icon="mid-alert"
                      color="error"
                      variant="tonal"
                      @click="openResolveCurrentConfilctModal(item.validations?.['duplicate'])"
                    >
                      {{ t('lcp.duplicate') }}
                      <template #prepend>
                        <v-icon color="error">mdi-alert</v-icon>
                      </template>
                    </v-chip>
                    <v-chip
                      v-if="item.validations?.['exists']"
                      prepend-icon="mid-alert"
                      color="error"
                      variant="tonal"
                      @click="openResolveCurrentConfilctModal(item.validations?.['exists'])"
                    >
                      {{ t('lcp.already_exists') }}
                      <template #prepend>
                        <v-icon color="error">mdi-alert</v-icon>
                      </template>
                    </v-chip>
                  </div>
                </template>
              </div>
            </td>
            <td>
              <RoleSelect
                bg-color="surface"
                v-model="item.role"
                class="role-select v-select--active-menu"
                :options="roleOptions"
                hide-details
                :readonly="isReadonly(item)"
                :disabled="isReadonly(item)"
                variant="solo"
                density="compact"
                height="48"
                :id="item.key"
                :name="item.key"
                :menu-icon="SelectIcon"
                @update:modelValue="($event) => onModelValueUpdated($event, index)"
              >
              </RoleSelect>
            </td>

            <td>
              <EditableText
                v-model="item.email"
                :label="t('lcp.add_email') + (item.role !== 'student' ? ' *' : '')"
                :key="item.key"
                :readonly="isReadonly(item)"
                @blur="updateEmail(item)"
                @editing="(isEditing) => (item.editing = isEditing)"
                @update:modelValue="($event) => onModelValueUpdated($event, index)"
                :rules="emailRule(item, t)"
                :errorMessages="item.validations?.['invalid-email']?.message"
              />
            </td>
            <td>
              <GroupAssignment
                :organization-uid="organization.uid"
                v-model:model-value="item.groupUid"
                v-model:model-name="item.groupName"
                :rules="groupRule(isGroupLevel, t)"
                :disabled="isReadonly(item)"
              />
            </td>
            <td>
              <div class="d-flex justify-end align-center flex-nowrap ga-2">
                <v-icon
                  v-if="item.status === 'error' && canRetryCreate"
                  color="error"
                  :disabled="(conflicts?.length ?? 0) > 0"
                  @click="emits('retryCreateSingleUser', item)"
                >
                  mdi-reload
                </v-icon>

                <v-icon v-if="item.status === 'error'" color="error" @click="toggleExpand(internalItem)">
                  mdi-alert
                </v-icon>
                <v-icon v-if="item.status === 'success'" color="success">mdi-check</v-icon>
                <v-icon v-if="item.status === 'processing'" color="primary">mdi-spin mdi-loading</v-icon>
                <v-icon
                  v-if="!canRetryCreate && item.status !== 'success' && item.status !== 'processing'"
                  @click="() => emits('removeUser', item.key)"
                >
                  mdi-close
                </v-icon>
              </div>
            </td>
          </tr>
        </template>
        <template #expanded-row="{ item }">
          <td :colspan="6" class="px-4 bg-red-lighten-5">
            <v-alert type="error" variant="text" density="compact" icon="mdi-alert">
              {{ item.errorMessages }}
            </v-alert>
          </td>
        </template>
        <template #bottom>
          <div class="px-4" style="border-top: 1px solid rgb(var(--v-theme-outline))">
            <v-btn :disabled="readonly" variant="text" prepend-icon="mdi-plus" @click="addNewUser">{{
              t('lcp.add_user')
            }}</v-btn>
          </div>
        </template>
      </v-data-table-virtual>
    </v-col>
  </v-row>
</template>
<style scoped lang="scss"></style>
